import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../store';
import {
  accountStatusOptions,
  options0To100,
  optionsAvailability,
  postOptions
} from '../../../enums/enums';
import { getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SelectGroup from '../../../components/casino/SelectGroup';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import CusDateTimePicker from '../../../components/material-ui/CusDateTimePicker';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import CusInput from '../../../components/material-ui/CusInput';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import CusButton from '../../../components/material-ui/CusButton';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import FlexContainer from '../../../components/material-ui/adaptive/FlexContainer';

const playersOptions = [
  { label: '——', value: 'skip' },
  { label: 'by Email', value: 'email' },
  { label: 'via Telegram', value: 'telegram' }
];

const defaultFilter = {
  groupId: '',
  useSubgroups: true,
  dateRegFrom: null,
  dateRegTo: null,
  onlineAfterDate: null,
  onlineBeforeDate: null,
  // regType: 'skip',
  userStatus: 'skip',
  phoneNumber: 'skip',
  regRefcodeId: null,
  depositCountFrom: 'skip',
  depositCountTo: 'skip',
  depositCurrency: 'skip',
  depositAmountMin: '0',
  depositAmountMax: '0',
  depositDateFrom: null,
  depositDateTo: null,
  depositIsLocked: 'skip',
  runGame: 'skip',
  payoutInvoice: 'skip',
  unreadMsg: 'skip',
  pressBotKey: 'skip',
  botDeleted: 'skip'
};

export default function MailingItemPayload({ onFindClick, saveFilter, setSearchDone, readOnly }) {
  const { t } = useTranslation();
  const { isMobile } = useBootstrapSize();
  const currenciesAll = useAppSelector((state) => state.user.info.currency);
  const isLayout = useAppSelector((state) => state.customizer.isLayout);
  const mainGroupId = useAppSelector((state) => state.user.group.main?.groupId);

  const currencyOptions = useMemo(() => {
    const options = Object.keys(currenciesAll).map((el) => ({ value: el, label: el }));
    options.unshift({ label: t('——'), value: 'skip' });
    return options;
  }, [currenciesAll]);

  const optionsDeposit = useMemo(() => {
    const optionsDeposit = [...options0To100];
    optionsDeposit.unshift({ label: t('——'), value: 'skip' });
    return optionsDeposit;
  }, []);

  defaultFilter.groupId = mainGroupId || '';

  const [filter, setFilter] = useState(saveFilter || defaultFilter);

  const isBoxed = isLayout === 'boxed';
  const startDate = filter.dateRegFrom;
  const endDate = filter.dateRegTo;
  const lastOnline = filter.onlineBeforeDate;
  const wentOnline = filter.onlineAfterDate;
  const filterGroup = filter.groupId;
  const disabledDeposit = +filter.depositCountTo == 0;

  const gridSize = { xs: 12, sm: 6 };
  const gridSize2 = { xs: 12, sm: 6, md: 4 };
  const gridSize2p5 = { xs: 12, sm: 6, md: 3 };
  const gridSize3Last = { xs: 12, sm: 6, md: 3 };

  const isVerySmall = isBoxed;

  const onResetFilter = () => {
    const resetFilter = { ...defaultFilter, dateRegFrom: null, dateRegTo: null };
    setFilter(resetFilter);
  };

  useLayoutEffect(() => {
    if (filter.groupId) return;
    setFilter((prev) => ({ ...prev, groupId: prev.groupId || mainGroupId }));
  }, [mainGroupId]);

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    setSearchDone(false);
  }, [filterKey]);

  return (
    <Box mt={1}>
      <Stack gap={0.75}>
        <Typography fontWeight={500}>{t('Filter for selecting mailing recipients')}</Typography>
        <Grid container spacing={0.75}>
          <Grid size={12}>
            <Stack
              display={'flex'}
              direction={'row'}
              gap={1}
              alignItems={'center'}
              flexWrap={'wrap'}
              mb={1}>
              <SelectGroup
                title="Group"
                hideTooltip
                selectGroupId={filterGroup}
                readOnly={readOnly}
                setSelectGroupId={(val) => setFilter((prev) => ({ ...prev, groupId: val }))}
              />
              <CusCheckbox
                label={'all subgroups'}
                checked={filter.useSubgroups}
                onChange={() =>
                  setFilter((prev) => ({ ...prev, useSubgroups: !prev.useSubgroups }))
                }
                disabled={readOnly}
              />
            </Stack>
          </Grid>
          <Grid size={12} container rowSpacing={2} columnSpacing={1}>
            <Grid size={gridSize}>
              <CusDateTimePicker
                label="Registration date, from"
                value={startDate}
                onChange={(val) => setFilter((prev) => ({ ...prev, dateRegFrom: val }))}
                formatChange="YYYY-MM-DD HH:mm:ss"
                clearable
                disabled={readOnly}
              />
            </Grid>
            <Grid size={gridSize}>
              <CusDateTimePicker
                label="Registration date, by"
                value={endDate}
                onChange={(val) => setFilter((prev) => ({ ...prev, dateRegTo: val }))}
                formatChange="YYYY-MM-DD HH:mm:ss"
                errorText={
                  startDate &&
                  endDate &&
                  new Date(endDate).getTime() <= new Date(startDate).getTime()
                    ? t('End date must be later than start date')
                    : ''
                }
                clearable
                disabled={readOnly}
              />
            </Grid>
          </Grid>
          <Grid size={12}>
            <Box px={2} py={1} my={1} sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 7, md: 4 }}>
                  <Stack>
                    <Stack direction={'row'} gap={1}>
                      <CusLabelSelect
                        label={'Deposits from'}
                        size={'grow'}
                        toolTipText="number of deposits"
                        value={String(filter.depositCountFrom)}
                        options={optionsDeposit}
                        onChange={(val) =>
                          setFilter((prev) => ({ ...prev, depositCountFrom: val }))
                        }
                        isVerySmall
                        disabled={readOnly}
                      />
                      <CusLabelSelect
                        label={'Deposits to'}
                        size={'grow'}
                        value={String(filter.depositCountTo)}
                        onChange={(val) => setFilter((prev) => ({ ...prev, depositCountTo: val }))}
                        options={optionsDeposit}
                        isVerySmall
                        disabled={readOnly}
                      />
                    </Stack>
                    {console.log(
                      'filter.depositCountFrom > filter.depositCountTo',
                      filter.depositCountFrom,
                      filter.depositCountTo
                    )}
                    <ShowContainer
                      condition={
                        filter.depositCountFrom != 'skip' && filter.depositCountTo != 'skip'
                      }>
                      <Typography color="error" fontWeight={900}>
                        {filter.depositCountFrom > filter.depositCountTo
                          ? t('The "from" value must be less than or equal to the "to" value.')
                          : ''}
                      </Typography>
                    </ShowContainer>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, sm: 5, md: 2 }}>
                  <CusLabelSelect
                    isVerySmall={isVerySmall}
                    label="Currency"
                    fullWidth
                    value={filter.depositCurrency}
                    onChange={(val) => {
                      if (val == 'skip') {
                        setFilter((prev) => ({
                          ...prev,
                          depositAmountMin: 0,
                          depositAmountMax: 0,
                          depositCurrency: val
                        }));
                        return;
                      }
                      setFilter((prev) => ({ ...prev, depositCurrency: val }));
                    }}
                    disabled={readOnly}
                    options={currencyOptions}
                  />
                </Grid>
                <Grid size={gridSize2p5}>
                  <CusInput
                    isVerySmall={isVerySmall}
                    label={'Deposit amount from'}
                    tooltipText={'0 - the amount is not used'}
                    type="number"
                    value={String(+filter.depositAmountMin)}
                    onChange={(e) =>
                      setFilter((prev) => ({
                        ...prev,
                        depositAmountMin: Math.abs(e.target.value)
                      }))
                    }
                    inputProps={{ min: 0 }}
                    disabled={filter.depositCurrency == 'skip' || disabledDeposit || readOnly}
                  />
                </Grid>
                <Grid size={gridSize2p5}>
                  <CusInput
                    isVerySmall={isVerySmall}
                    label={'Deposit amount by'}
                    type="number"
                    tooltipText={'0 - the amount is not used'}
                    value={String(+filter.depositAmountMax)}
                    onChange={(e) =>
                      setFilter((prev) => ({
                        ...prev,
                        depositAmountMax: Math.abs(e.target.value)
                      }))
                    }
                    inputProps={{ min: 0 }}
                    disabled={filter.depositCurrency == 'skip' || disabledDeposit || readOnly}
                  />
                </Grid>
                <Grid size={gridSize} pt={1}>
                  <CusDatePicker
                    isVerySmall={isVerySmall}
                    label={'Deposit start date'}
                    value={filter.depositDateFrom}
                    onChange={(val) => setFilter((prev) => ({ ...prev, depositDateFrom: val }))}
                    formatChange="YYYY-MM-DD"
                    clearable
                    disabled={disabledDeposit || readOnly}
                  />
                </Grid>
                <Grid size={gridSize} py={1}>
                  <CusDatePicker
                    isVerySmall={isVerySmall}
                    label={'Deposit end date'}
                    value={filter.depositDateTo}
                    onChange={(val) => setFilter((prev) => ({ ...prev, depositDateTo: val }))}
                    formatChange="YYYY-MM-DD"
                    clearable
                    disabled={disabledDeposit || readOnly}
                    errorStartDate={filter.depositDateTo}
                    errorText={
                      filter.depositDateFrom &&
                      filter.depositDateTo &&
                      new Date(filter.depositDateTo) < new Date(filter.depositDateFrom)
                        ? t('End date must be later than start date')
                        : ''
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={gridSize}>
            <CusDatePicker
              label="Went online after date"
              value={wentOnline}
              onChange={(val) => setFilter((prev) => ({ ...prev, onlineAfterDate: val }))}
              formatChange="YYYY-MM-DD"
              clearable
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize} mt={isMobile ? 1 : 0}>
            <CusDatePicker
              label="Last online until"
              value={lastOnline}
              onChange={(val) => setFilter((prev) => ({ ...prev, onlineBeforeDate: val }))}
              formatChange="YYYY-MM-DD"
              clearable
              errorText={
                wentOnline && lastOnline && new Date(lastOnline) < new Date(wentOnline)
                  ? t('End date must be later than start date')
                  : ''
              }
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusInput
              isVerySmall={isVerySmall}
              label={'Registered under refcode'}
              value={filter.regRefcodeId}
              type="number"
              inputProps={{ min: 0 }}
              hideArrows
              onChange={(e) =>
                setFilter((prev) => ({ ...prev, regRefcodeId: e.target.value || null }))
              }
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'There are unread messages in the bot'}
              value={filter.unreadMsg}
              onChange={(val) => setFilter((prev) => ({ ...prev, unreadMsg: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Pressed any button in the bot'}
              value={filter.pressBotKey}
              onChange={(val) => setFilter((prev) => ({ ...prev, pressBotKey: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'There is a deposit block'}
              value={filter.depositIsLocked}
              onChange={(val) => setFilter((prev) => ({ ...prev, depositIsLocked: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Launched any game'}
              value={filter.runGame}
              onChange={(val) => setFilter((prev) => ({ ...prev, runGame: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Created an invoice for input'}
              value={filter.payoutInvoice}
              onChange={(val) => setFilter((prev) => ({ ...prev, payoutInvoice: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>

          {/* <Grid size={gridSize3Last}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Player is registered'}
              options={playersOptions}
              onChange={(val) => setFilter((prev) => ({ ...prev, regType: val }))}
              value={filter.regType}
              disabled={readOnly}
            />
          </Grid> */}
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'The bot has been marked as deleted'}
              value={filter.botDeleted}
              onChange={(val) => setFilter((prev) => ({ ...prev, botDeleted: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Account status'}
              value={filter.userStatus}
              onChange={(val) => setFilter((prev) => ({ ...prev, userStatus: val }))}
              options={accountStatusOptions}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'A phone number was shared in a bot'}
              value={filter.phoneNumber}
              onChange={(val) => setFilter((prev) => ({ ...prev, phoneNumber: val }))}
              options={optionsAvailability}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          gap={1}
          justifyContent="flex-end"
          mt={2}
          alignItems={isMobile ? undefined : 'center'}>
          <CusButton
            text="Reset filters"
            variant="outlined"
            sx={{ px: 5 }}
            fullWidth={isMobile}
            onClick={onResetFilter}
            disabled={readOnly}
          />
          <CusButton
            text="Find"
            variant="contained"
            sx={{ px: 5 }}
            fullWidth={isMobile}
            onClick={() => onFindClick(filter)}
            disabled={readOnly}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
